import React, { useCallback, useState } from 'react';
import { Menu, MenuItem, Typography } from '@mui/material';
import { useParams } from 'react-router-dom';

import {
  TableRow,
  TableCell,
  IconButton,
  Divider,
  MoreVertIcon,
  InfoOutlinedIcon,
} from '@occc-app/design-system';
import { RetailerNetworkLocationDataFragment } from '@occc/graphql';
import { Can } from '@occc-app/App/PolicyBasedAccessControl';
import { UpdateRetailerNetworkUser } from '@occc-app/App/PolicyBasedAccessControl/userActions';

import { InheritedLocationInfoDialog } from '../../../components/inherited-location-info-dialog.component';

type MenuItem = 'locationDetails' | 'removeUserFromLocation';

type RetailerNetworkUserDetailsLocationsTableRowProps = {
  location: RetailerNetworkLocationDataFragment;
  isLocationInheritedFromGroup: boolean;
  handleOpenLocationDetailsPage: (locationUuid: string) => void;
  handleRemoveUserFromLocation: (locationUuid: string) => void;
  isCurrentUserPermittedToDisassociate: boolean;
};

export const RetailerNetworkUserDetailsLocationsTableRow = ({
  location,
  isLocationInheritedFromGroup,
  handleOpenLocationDetailsPage,
  handleRemoveUserFromLocation,
  isCurrentUserPermittedToDisassociate,
}: RetailerNetworkUserDetailsLocationsTableRowProps) => {
  const { networkId } = useParams<{ networkId: string }>();
  const [actionsForUuid, setActionsForUuid] = useState<string | null>();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [isInheritedLocationInfoModalOpen, setIsInheritedLocationInfoModalOpen] =
    useState<boolean>(false);

  const getHandlerForAdditionalActions =
    (location: RetailerNetworkLocationDataFragment) => (event: React.MouseEvent<HTMLElement>) => {
      event.stopPropagation();

      setAnchorEl(event.currentTarget);
      setActionsForUuid(location.uuid);
    };

  const handleClose = useCallback((event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setAnchorEl(null);
    setActionsForUuid(null);
  }, []);

  const onInheritedLocationInfoClick = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    handleClose(event);
    setIsInheritedLocationInfoModalOpen(true);
  };

  const onInheritedLocationInfoModalClose = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setIsInheritedLocationInfoModalOpen(false);
  };

  const onMenuNavigationClick = (itemName: MenuItem) => (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();

    if (itemName === 'locationDetails') {
      handleOpenLocationDetailsPage(location.uuid);
    }

    if (itemName === 'removeUserFromLocation') {
      return !isLocationInheritedFromGroup
        ? handleRemoveUserFromLocation(location.uuid)
        : onInheritedLocationInfoClick(event);
    }

    handleClose(event);
  };

  const siteSystemConfiguration =
    location.siteSystemConfiguration.length > 0 ? location.siteSystemConfiguration[0] : null;

  const locationCity = location?.address?.city || '';
  const locationState = location?.address?.state || '';
  const locationStreet = location?.address?.street1 || '';
  const locationZip = location?.address?.zipCode || '';

  return (
    <TableRow key={location.uuid} onClick={() => handleOpenLocationDetailsPage(location.uuid)}>
      <TableCell>{siteSystemConfiguration?.identifier}</TableCell>
      <TableCell>{location.name}</TableCell>
      <TableCell>
        {locationStreet}
        <br />
        {`${locationCity}, ${locationState} ${locationZip}`}
      </TableCell>
      <TableCell>{location?.phoneNumbers?.[0]?.phoneNumber}</TableCell>

      <TableCell align='right'>
        <IconButton onClick={getHandlerForAdditionalActions(location)} sx={{ p: 0 }}>
          <MoreVertIcon />
        </IconButton>
        <Menu
          open={location.uuid === actionsForUuid}
          onClose={handleClose}
          anchorEl={anchorEl}
          PaperProps={{
            sx: {
              borderRadius: '4px',
            },
          }}
        >
          <MenuItem onClick={onMenuNavigationClick('locationDetails')}>
            View Location Details
          </MenuItem>

          <Can I={UpdateRetailerNetworkUser({ uuid: networkId || '' })}>
            {isCurrentUserPermittedToDisassociate && [
              <Divider key='divider' />,
              <MenuItem
                onClick={onMenuNavigationClick('removeUserFromLocation')}
                key='disassociateUserFromLocation'
              >
                {isLocationInheritedFromGroup ? (
                  <>
                    <Typography
                      color='textSecondary'
                      sx={{ cursor: 'not-allowed' }}
                      variant='body2'
                    >
                      Remove User from Location
                    </Typography>
                    <InfoOutlinedIcon color='primary' sx={{ ml: 1 }} />
                  </>
                ) : (
                  'Remove User from Location'
                )}
              </MenuItem>,
            ]}
          </Can>
        </Menu>
      </TableCell>
      {isLocationInheritedFromGroup && (
        <InheritedLocationInfoDialog
          open={isInheritedLocationInfoModalOpen}
          onClose={onInheritedLocationInfoModalClose}
        />
      )}
    </TableRow>
  );
};
